import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faHeart,
  faTrash,
  faExternalLinkAlt,
  faTruckFast,
  faArrowLeft,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { resolveWatchlistRetailer } from '../utils/watchlistRetailer';
import { openExternalLink } from '../utils/openExternalLink';
import { trackMetaInitiateCheckout } from '../utils/analytics';
import '../StylesComponents/wishlist-side-panel.css';

function itemPrice(item) {
  return Number(item.productPrice ?? item.price ?? 0) || 0;
}

function itemMsrp(item) {
  const sale = itemPrice(item);
  const original = Number(item.originalPrice ?? 0) || 0;
  return original > sale ? original : sale;
}

function isPartneredWatchlistItem(item) {
  return !!(item?.isPartnered || item?.size === 'ONESIZE');
}

function WishlistSidePanel({ isOpen, onClose, currentUser }) {
  const { wishlistItems, loading, removeFromWishlist } = useWishlist();
  const { convertPrice, getCurrencySymbol } = useCurrency();

  const [checkoutMode, setCheckoutMode] = useState(false);
  const [checkoutRetailer, setCheckoutRetailer] = useState(null);
  const [openedIds, setOpenedIds] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) {
      setCheckoutMode(false);
      setCheckoutRetailer(null);
      setOpenedIds(new Set());
    }
  }, [isOpen]);

  const formatPriceLocal = (price) => {
    if (price === undefined || price === null) return '0.00';
    let numericPrice = price;
    if (typeof price === 'string') {
      numericPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
    }
    if (isNaN(numericPrice)) return '0.00';
    const converted = convertPrice(numericPrice);
    return `${getCurrencySymbol()}${converted}`;
  };

  const handleRemove = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    removeFromWishlist(itemId);
  };

  const groupedItems = useMemo(() => {
    return wishlistItems.reduce((acc, item) => {
      const retailer = resolveWatchlistRetailer(item);
      if (!acc[retailer]) acc[retailer] = [];
      acc[retailer].push(item);
      return acc;
    }, {});
  }, [wishlistItems]);

  const metrics = useMemo(() => {
    const dealTotal = wishlistItems.reduce((sum, item) => sum + itemPrice(item), 0);
    const msrpTotal = wishlistItems.reduce((sum, item) => sum + itemMsrp(item), 0);
    const savings = Math.max(msrpTotal - dealTotal, 0);
    const savingsPct = msrpTotal > 0 ? Math.round((savings / msrpTotal) * 100) : 0;
    const retailerCount = Object.keys(groupedItems).length;

    let shippingUnlocked = 0;
    let shippingClose = null;
    Object.entries(groupedItems).forEach(([retailer, items]) => {
      const threshold = items[0]?.freeShippingThreshold || 150;
      const brandTotal = items.reduce((sum, item) => sum + itemPrice(item), 0);
      const remaining = Math.max(threshold - brandTotal, 0);
      if (remaining <= 0) {
        shippingUnlocked += 1;
      } else if (!shippingClose || remaining < shippingClose.remaining) {
        shippingClose = { retailer, remaining };
      }
    });

    return {
      itemCount: wishlistItems.length,
      dealTotal,
      msrpTotal,
      savings,
      savingsPct,
      retailerCount,
      shippingUnlocked,
      shippingClose,
      hasMsrpSavings: savings > 0 && msrpTotal > dealTotal,
    };
  }, [wishlistItems, groupedItems]);

  const checkoutItems = useMemo(() => {
    if (!checkoutRetailer) return wishlistItems;
    return wishlistItems.filter((item) => resolveWatchlistRetailer(item) === checkoutRetailer);
  }, [wishlistItems, checkoutRetailer]);

  const checkoutGrouped = useMemo(() => {
    return checkoutItems.reduce((acc, item) => {
      const retailer = resolveWatchlistRetailer(item);
      if (!acc[retailer]) acc[retailer] = [];
      acc[retailer].push(item);
      return acc;
    }, {});
  }, [checkoutItems]);

  const openedCount = checkoutItems.filter((item) => openedIds.has(item._id)).length;
  const nextUnopened = checkoutItems.find((item) => item.affiliateUrl && !openedIds.has(item._id));

  const startCheckout = (retailer = null) => {
    const items = retailer
      ? wishlistItems.filter((item) => resolveWatchlistRetailer(item) === retailer)
      : wishlistItems;
    trackMetaInitiateCheckout(items);
    setCheckoutRetailer(retailer);
    setOpenedIds(new Set());
    setCheckoutMode(true);
  };

  const exitCheckout = () => {
    setCheckoutMode(false);
    setCheckoutRetailer(null);
    setOpenedIds(new Set());
  };

  const openDeal = (item) => {
    if (!item?.affiliateUrl) return;
    openExternalLink(item.affiliateUrl);
    setOpenedIds((prev) => {
      const next = new Set(prev);
      next.add(item._id);
      return next;
    });
  };

  const openNextDeal = () => {
    if (!nextUnopened) return;
    openDeal(nextUnopened);
  };

  const itemSavings = (item) => {
    const sale = itemPrice(item);
    const msrp = itemMsrp(item);
    const saved = Math.max(msrp - sale, 0);
    return saved > 0 ? saved : 0;
  };

  const itemBrandLabel = (item) => {
    const brand = String(item.productBrand || item.brand || '').trim();
    const retailer = resolveWatchlistRetailer(item);
    if (!brand) return null;
    if (brand.toLowerCase() === retailer.toLowerCase()) return null;
    return brand;
  };

  return (
    <>
      <div className={`wishlist-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}></div>
      <div className={`wishlist-side-panel ${isOpen ? 'open' : ''}`}>
        <div className="wishlist-header-technical">
          <div className="wishlist-status">
            <span className="pulse-dot"></span>
            {checkoutMode
              ? `Checkout // ${openedCount} of ${checkoutItems.length} opened`
              : `My Watchlist (${wishlistItems.length} items)`}
          </div>
          <h2>{checkoutMode ? 'Checkout' : 'Watchlist'}</h2>
          <button className="wishlist-close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="wishlist-content-technical">
          {loading ? (
            <div className="wishlist-loading"><p className="empty-text">Syncing...</p></div>
          ) : wishlistItems.length === 0 ? (
            <div className="wishlist-empty-state">
              <div className="empty-state-container">
                <div className="technical-empty-box">
                  <FontAwesomeIcon icon={faHeart} className="wishlist-empty-icon" />
                  <div className="scanline"></div>
                </div>
                <div className="empty-logistics-node">
                  <span className="node-label">STATUS</span>
                  <span className="node-value">ZERO_SIGNALS</span>
                </div>
                <p className="empty-text">ARCHIVE_EMPTY: NO_SAVED_ASSETS_IN_WATCHLIST</p>
                <button className="return-to-feed-brutalist" onClick={onClose}>
                  GO_TO_MARKET
                </button>
              </div>
            </div>
          ) : checkoutMode ? (
            <div className="checkout-queue">
              <div className="checkout-queue-toolbar">
                <button type="button" className="checkout-back-btn" onClick={exitCheckout}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                  BACK TO WATCHLIST
                </button>
                <p className="checkout-queue-note">
                  You check out on the retailer&apos;s site — one tab per deal.
                </p>
              </div>

              <div className="checkout-progress-row">
                <span className="checkout-progress-label">
                  {openedCount} of {checkoutItems.length} opened
                  {checkoutRetailer ? ` // ${checkoutRetailer.toUpperCase()}` : ''}
                </span>
                <div className="checkout-progress-bg">
                  <div
                    className="checkout-progress-bar"
                    style={{
                      width: `${checkoutItems.length ? (openedCount / checkoutItems.length) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>

              {Object.entries(checkoutGrouped).map(([brand, items]) => (
                <div key={brand} className="checkout-retailer-group">
                  <div className="checkout-retailer-label">
                    <span className="brand-tag">RETAILER</span>
                    <h3 className="brand-title">{brand.toUpperCase()}</h3>
                  </div>
                  <div className="checkout-items-list">
                    {items.map((item) => {
                      const opened = openedIds.has(item._id);
                      const saved = itemSavings(item);
                      return (
                        <div
                          key={item._id}
                          className={`checkout-item ${opened ? 'is-opened' : ''}`}
                        >
                          <div className="checkout-item-thumb">
                            <img
                              src={item.productImage || item.image}
                              alt=""
                            />
                          </div>
                          <div className="checkout-item-body">
                            {itemBrandLabel(item) && (
                              <span className="item-brand-label">{itemBrandLabel(item)}</span>
                            )}
                            <h4 className="checkout-item-name">
                              {item.productName || item.name}
                            </h4>
                            <div className="checkout-item-meta">
                              <span>{formatPriceLocal(itemPrice(item))}</span>
                              {saved > 0 && (
                                <span className="checkout-savings-chip">
                                  SAVE {formatPriceLocal(saved)}
                                </span>
                              )}
                              {!isPartneredWatchlistItem(item) && item.size && (
                                <span className="checkout-size">SIZE {item.size}</span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`checkout-open-deal-btn ${opened ? 'opened' : ''}`}
                            onClick={() => openDeal(item)}
                            disabled={!item.affiliateUrl}
                          >
                            {opened ? (
                              <>
                                <FontAwesomeIcon icon={faCheck} /> OPENED
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faExternalLinkAlt} /> OPEN DEAL
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="wishlist-brand-groups">
              {Object.entries(groupedItems).map(([brand, items]) => {
                const threshold = items[0]?.freeShippingThreshold || 150;
                const brandTotal = items.reduce((sum, item) => sum + itemPrice(item), 0);
                const progress = Math.min((brandTotal / threshold) * 100, 100);
                const remaining = Math.max(threshold - brandTotal, 0);

                return (
                  <div key={brand} className="brand-group-section">
                    <div className="brand-group-header">
                      <div className="brand-name-wrap">
                        <span className="brand-tag">RETAILER</span>
                        <Link
                          to={`/all-brands?retailers=${encodeURIComponent(brand)}`}
                          className="brand-title-link"
                          onClick={onClose}
                        >
                          <h3 className="brand-title">{brand.toUpperCase()}</h3>
                        </Link>
                      </div>

                      <div className="shipping-incentive-box">
                        <div className="shipping-status-row">
                          <span className={`shipping-label ${remaining <= 0 ? 'eligible' : ''}`}>
                            {remaining > 0
                              ? `Spend ${formatPriceLocal(remaining)} more for FREE SHIPPING`
                              : '✓ FREE SHIPPING UNLOCKED'}
                          </span>
                          <span className="shipping-pct">{Math.round(progress)}%</span>
                        </div>
                        <div className="shipping-progress-bg">
                          <div
                            className="shipping-progress-bar"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        {items.length > 1 && (
                          <button
                            type="button"
                            className="open-retailer-deals-btn"
                            onClick={() => startCheckout(brand)}
                          >
                            <FontAwesomeIcon icon={faTruckFast} />
                            CHECKOUT {brand.toUpperCase()}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="brand-items-list">
                      {items.map((item, idx) => (
                        <div key={item._id} className={`wishlist-item-fullwidth ${!isPartneredWatchlistItem(item) && item.isAvailable === false ? 'unavailable' : ''}`}>
                          <Link to={`/product/${item.productId}`} className="item-main-link" onClick={onClose}>
                            <div className="item-thumbnail">
                              <img src={item.productImage || item.image} alt={item.productName} />
                            </div>
                            
                            <div className="item-details-expanded">
                            <div className="item-identity">
                                <span className="item-index-tech">#{String(idx + 1).padStart(2, '0')}</span>
                                {itemBrandLabel(item) && (
                                  <span className="item-brand-label">{itemBrandLabel(item)}</span>
                                )}
                                <h4 className="item-name-expanded">{item.productName || item.name}</h4>
                                {!isPartneredWatchlistItem(item) && item.isAvailable === false && (
                                  <span className="availability-warning">SIZE NO LONGER AVAILABLE</span>
                                )}
                              </div>

                              <div className="item-meta-row">
                                {!isPartneredWatchlistItem(item) && (
                                  <div className="meta-node">
                                    <span className="meta-label">SIZE</span>
                                    <span className="meta-value">{item.size || 'M'}</span>
                                  </div>
                                )}
                                <div className="meta-node">
                                  <span className="meta-label">PRICE</span>
                                  <span className="meta-value highlight">{formatPriceLocal(itemPrice(item))}</span>
                                </div>
                                {item.originalPrice && item.originalPrice > itemPrice(item) && (
                                  <div className="meta-node">
                                    <span className="meta-label">MSRP</span>
                                    <span className="meta-value strikethrough">{formatPriceLocal(item.originalPrice)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Link>

                          <div className="item-quick-actions">
                            <button
                              className="action-btn-minimal"
                              onClick={() =>
                                item.affiliateUrl &&
                                openExternalLink(item.affiliateUrl)
                              }
                              title="Go to Retailer"
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </button>
                            <button
                              type="button"
                              className="action-btn-minimal delete"
                              onClick={(e) => handleRemove(e, item._id)}
                              aria-label={`Remove ${item.productName || item.name || 'item'} from Watchlist`}
                              title="Remove from Watchlist"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {wishlistItems.length > 0 && !checkoutMode && (
          <div className="wishlist-footer-frozen">
            <div className="wishlist-metrics">
              <div className="wishlist-metric">
                <span className="wishlist-metric-label">ITEMS</span>
                <span className="wishlist-metric-value">{metrics.itemCount}</span>
              </div>
              <div className="wishlist-metric">
                <span className="wishlist-metric-label">RETAILERS</span>
                <span className="wishlist-metric-value">{metrics.retailerCount}</span>
              </div>
              <div className="wishlist-metric">
                <span className="wishlist-metric-label">DEAL TOTAL</span>
                <span className="wishlist-metric-value">
                  {formatPriceLocal(metrics.dealTotal)}
                </span>
              </div>
              {metrics.hasMsrpSavings && (
                <div className="wishlist-metric wishlist-metric--msrp">
                  <span className="wishlist-metric-label">MSRP</span>
                  <span className="wishlist-metric-value strikethrough">
                    {formatPriceLocal(metrics.msrpTotal)}
                  </span>
                </div>
              )}
              {metrics.hasMsrpSavings && (
                <div className="wishlist-metric wishlist-metric--savings">
                  <span className="wishlist-metric-label">YOU SAVE</span>
                  <span className="wishlist-metric-value">
                    {formatPriceLocal(metrics.savings)}
                    <span className="wishlist-metric-pct">({metrics.savingsPct}%)</span>
                  </span>
                </div>
              )}
            </div>

            {metrics.shippingUnlocked > 0 ? (
              <p className="wishlist-shipping-note eligible">
                Free shipping unlocked at {metrics.shippingUnlocked} retailer
                {metrics.shippingUnlocked === 1 ? '' : 's'}
              </p>
            ) : metrics.shippingClose ? (
              <p className="wishlist-shipping-note">
                {formatPriceLocal(metrics.shippingClose.remaining)} more at{' '}
                {metrics.shippingClose.retailer} unlocks free shipping
              </p>
            ) : null}

            <button
              type="button"
              className="purchase-all-btn-brutalist"
              onClick={() => startCheckout(null)}
            >
              <span className="btn-label">CHECKOUT</span>
              <span className="btn-stats">
                {metrics.itemCount} DEALS // ONE TAB PER CLICK
              </span>
            </button>
          </div>
        )}

        {wishlistItems.length > 0 && checkoutMode && (
          <div className="wishlist-footer-frozen checkout-footer">
            <button
              type="button"
              className="purchase-all-btn-brutalist"
              onClick={openNextDeal}
              disabled={!nextUnopened}
            >
              <span className="btn-label">
                {nextUnopened ? 'OPEN NEXT' : 'ALL OPENED'}
              </span>
              <span className="btn-stats">
                {openedCount} / {checkoutItems.length} DEALS
              </span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default WishlistSidePanel;
